import { useState, useEffect } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import api from '@/shared/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Separator } from '@/shared/ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Check, X, Settings2 } from 'lucide-react';

const MENU_KEYS = [
  { key: 'regions', label: 'Regions', description: 'Liste des regions dans le formulaire installateur et les filtres' },
  { key: 'types_actifs', label: 'Types d\'actifs', description: 'PAC, PV, Ballon, etc.' },
  { key: 'marques', label: 'Marques', description: 'Marques d\'equipements disponibles' },
  { key: 'civilites', label: 'Civilites', description: 'M., Mme, etc.' },
  { key: 'statuts_client', label: 'Statuts client', description: 'Prospect, Signe, Actif, etc.' },
];

export default function MenusPage() {
  const { user } = useAuth();
  const [menus, setMenus] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editValue, setEditValue] = useState('');

  const fetchMenus = async () => {
    try {
      const res = await api.get('/admin/menus');
      setMenus(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchMenus(); }, []);

  const addValue = async (key) => {
    if (!newValue.trim()) return;
    const current = menus[key] || [];
    if (current.includes(newValue.trim())) {
      toast.error('Cette valeur existe déjà');
      return;
    }
    try {
      await api.put(`/admin/menus/${key}`, { values: [...current, newValue.trim()] });
      toast.success('Valeur ajoutée');
      setNewValue('');
      fetchMenus();
    } catch { toast.error('Erreur'); }
  };

  const removeValue = async (key, idx) => {
    const current = [...(menus[key] || [])];
    const removed = current.splice(idx, 1);
    if (!window.confirm(`Supprimer "${removed}" ?`)) return;
    try {
      await api.put(`/admin/menus/${key}`, { values: current });
      toast.success('Valeur supprimée');
      fetchMenus();
    } catch { toast.error('Erreur'); }
  };

  const saveEdit = async (key, idx) => {
    if (!editValue.trim()) return;
    const current = [...(menus[key] || [])];
    current[idx] = editValue.trim();
    try {
      await api.put(`/admin/menus/${key}`, { values: current });
      toast.success('Valeur modifiée');
      setEditIdx(null);
      setEditValue('');
      fetchMenus();
    } catch { toast.error('Erreur'); }
  };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center text-neutral-500">Acces reserve aux administrateurs.</div>;
  }

  return (
    <div className="space-y-6" data-testid="menus-page">
      <div>
        <h1 className="text-xl font-semibold text-neutral-900">Personnalisation des menus</h1>
        <p className="text-sm text-neutral-500 mt-0.5">Gérez les valeurs des menus déroulants de l'application</p>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-400 py-8 text-center">Chargement...</p>
      ) : (
        <div className="space-y-4">
          {MENU_KEYS.map(({ key, label, description }) => {
            const values = menus[key] || [];
            const isOpen = editingKey === key;
            return (
              <Card key={key} data-testid={`menu-card-${key}`}>
                <CardHeader className="pb-2 cursor-pointer" onClick={() => setEditingKey(isOpen ? null : key)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm">{label}</CardTitle>
                      <p className="text-[11px] text-neutral-400 mt-0.5">{description} ({values.length} valeurs)</p>
                    </div>
                    <Settings2 className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                  </div>
                </CardHeader>
                {isOpen && (
                  <CardContent className="pt-0">
                    <Separator className="mb-3" />
                    <div className="space-y-1.5 mb-3 max-h-[300px] overflow-y-auto">
                      {values.map((v, idx) => (
                        <div key={idx} className="flex items-center gap-2 group">
                          {editIdx === idx && editingKey === key ? (
                            <>
                              <Input
                                className="h-7 text-[12px] flex-1"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && saveEdit(key, idx)}
                                autoFocus
                                data-testid={`edit-menu-input-${key}-${idx}`}
                              />
                              <button onClick={() => saveEdit(key, idx)} className="p-1 text-emerald-500 hover:bg-emerald-50 rounded"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => { setEditIdx(null); setEditValue(''); }} className="p-1 text-neutral-400 hover:bg-neutral-50 rounded"><X className="w-3.5 h-3.5" /></button>
                            </>
                          ) : (
                            <>
                              <span className="text-[12px] text-neutral-700 flex-1 py-1 px-2 rounded bg-neutral-50">{v}</span>
                              <button
                                onClick={() => { setEditIdx(idx); setEditValue(v); }}
                                className="p-1 text-neutral-300 hover:text-neutral-600 hover:bg-neutral-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`edit-menu-${key}-${idx}`}
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => removeValue(key, idx)}
                                className="p-1 text-neutral-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                data-testid={`delete-menu-${key}-${idx}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                      {values.length === 0 && <p className="text-[11px] text-neutral-400 text-center py-2">Aucune valeur</p>}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        className="h-8 text-[12px] flex-1"
                        placeholder="Ajouter une valeur..."
                        value={editingKey === key ? newValue : ''}
                        onChange={e => setNewValue(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addValue(key)}
                        data-testid={`add-menu-input-${key}`}
                      />
                      <Button size="sm" className="h-8" onClick={() => addValue(key)} data-testid={`add-menu-btn-${key}`}>
                        <Plus className="w-3 h-3 mr-1" /> Ajouter
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
